// OmniLink content data (copy & assets)

export const MEDIA = {
  hero: "https://static.prod-images.emergentagent.com/jobs/1093fcde-88b4-4edc-854f-5a2c7edecb88/images/a79669fb898e3e352b7790b3a2138fd287fec4d50d6786d7ec978585b1d8964f.png",
  showcase: "https://static.prod-images.emergentagent.com/jobs/1093fcde-88b4-4edc-854f-5a2c7edecb88/images/aec9fca52b673a4ee5cae482e24c69fa4c8f2fb59fb98fb7a4bfe3ec44aab16c.png",
  ecp: "https://static.prod-images.emergentagent.com/jobs/1093fcde-88b4-4edc-854f-5a2c7edecb88/images/f2679c6e8abab798473f67e81e65a461f801fc933a08c6e06fcd91f0bcefc21e.png",
};

export const NAV_LINKS = [
  { label: "Platform", href: "#platform", id: "nav-platform" },
  { label: "Hierarchy", href: "#hierarchy", id: "nav-hierarchy" },
  { label: "Capabilities", href: "#capabilities", id: "nav-capabilities" },
  { label: "Compliance", href: "#compliance", id: "nav-compliance" },
  { label: "ECP", href: "#pricing", id: "nav-ecp" },
  { label: "Contact", href: "#contact", id: "nav-contact" },
];

// Trust bar wordmarks intentionally left empty per brand cleanup.
// Heading remains; logos area renders blank until real operator references are approved.
export const TRUST_LABELS = [];

export const CAPABILITIES = [
  {
    title: "Record Keeping",
    description:
      "Centralised records with built-in document-expiry warnings — never miss a licence, medical or recurrent training renewal.",
    icon: "AlertOctagon",
  },
  {
    title: "Event Ledger",
    description:
      "Immutable, time-stamped ledger of every operational event — attributable to the exact user, exportable for any regulator.",
    icon: "ScrollText",
  },
  {
    title: "Flight Logging",
    description:
      "Per-aircraft and per-crew Flight Record Logs — captured digitally, signed-off in the cockpit, retained indefinitely.",
    icon: "PlaneTakeoff",
  },
  {
    title: "Monthly Schedule & Shifts",
    description:
      "Roster planning across FDC, Cabin Crew and Department Teams — month-at-a-glance with rule-aware shift assignment.",
    icon: "CalendarDays",
  },
  {
    title: "Vacation Bidding",
    description:
      "Structured vacation bidding for crews and staff — fair allocation, transparent priority, fully audit-trailed.",
    icon: "Umbrella",
  },
  {
    title: "Crew Duty & Rest Calculating",
    description:
      "Automated duty and rest-period calculation — flags conflicts before they happen, aligned with regulator limits.",
    icon: "TimerReset",
  },
  {
    title: "Fleet Management",
    description:
      "One source of truth for every tail, every department and every record — unified across the entire operation.",
    icon: "Plane",
  },
  {
    title: "Document Storing",
    description:
      "Versioned, role-scoped document storage — operational paperwork, crew files and certifications retrievable in seconds.",
    icon: "FolderArchive",
  },
  {
    title: "Audit Trailing",
    description:
      "Tamper-evident, attributable audit trail spanning every tier — from Controllers down to Department Teams.",
    icon: "FileCheck",
  },
  {
    title: "Employee Communications",
    description:
      "Role-aware in-portal messaging — broadcast updates, share documents and coordinate operations without leaving OmniLink.",
    icon: "MessageCircle",
  },
];

export const COMPLIANCE = [
  {
    code: "GACA",
    name: "General Authority of Civil Aviation",
    region: "Kingdom of Saudi Arabia",
    summary:
      "OmniLink is engineered to support GACA-compliant operations across Saudi-registered fleets. Record retention, crew records, flight documentation and reporting workflows align with GACA regulatory requirements.",
    icon: "Plane",
  },
  {
    code: "NCA",
    name: "National Cybersecurity Authority",
    region: "Kingdom of Saudi Arabia",
    summary:
      "The Kingdom's national authority responsible for cybersecurity strategy, governance and the Essential Cybersecurity Controls (ECC) that all national entities are expected to implement. OmniLink security controls are designed to map to the NCA's ECC framework.",
    icon: "ShieldCheck",
  },
  {
    code: "CSCC",
    name: "Critical Systems Cybersecurity Controls",
    region: "NCA — Extension of ECC",
    summary:
      "CSCC is an extension of the ECC issued by the NCA. It mandates elevated cybersecurity controls for systems classified as critical to the Kingdom's national security and essential services. OmniLink supports CSCC alignment for operators handling critical aviation systems.",
    icon: "Lock",
  },
];

export const HIERARCHY = [
  {
    tier: 1,
    name: "Controllers",
    overline: "External Oversight",
    summary:
      "The highest authority — independent oversight that provisions Main Admins, issues access credentials and supervises the entire operation from outside the org chart.",
    capabilities: ["Provision Main Admins", "Issue & revoke credentials", "External help & oversight"],
    icon: "ShieldCheck",
  },
  {
    tier: 2,
    name: "Main Admins",
    overline: "Operational Leadership",
    summary:
      "Created and authorised by Controllers. Own the day-to-day configuration of the portal — departments, policies, documents and lower-tier role provisioning.",
    capabilities: ["Configure portal modules", "Provision FDC & Cabin tiers", "Set operational policy"],
    icon: "Crown",
  },
  {
    tier: 3,
    name: "FDC",
    overline: "Flight Deck Crew",
    summary:
      "Captains and First Officers. Maintain Flight Record Logs, crew documents, schedules and digital sign-offs — every entry stored and attributable.",
    capabilities: ["Captain & First Officer profiles", "Flight Record Logs", "Crew warnings & documents"],
    icon: "Plane",
  },
  {
    tier: 4,
    name: "Cabin Crew",
    overline: "Cabin Operations",
    summary:
      "Supervisors, Pursers and Stewards. Manage schedules, crew warnings, documents and cabin records — orchestrated as a coordinated cabin tier.",
    capabilities: ["Supervisor / Purser / Steward", "Schedules & documents", "Crew warnings & records"],
    icon: "Users",
  },
  {
    tier: 5,
    name: "Department Teams",
    overline: "Employees",
    summary:
      "Maintenance, dispatch, scheduling, finance, HR and beyond. Every department gets a scoped workspace for its documents, schedules, warnings and records.",
    capabilities: ["Department-scoped access", "Documents & schedules", "Cross-team workflows"],
    icon: "Building2",
  },
];

export const PLANS = [
  {
    id: "template",
    badge: "STANDARD",
    name: "Template Plan",
    tagline: "Production-ready aviation operations, out of the box.",
    price: "Tailored",
    cadence: "to your fleet size",
    cta: "Discuss Template Plan",
    interest: "template",
    featured: false,
    features: [
      "OmniLink core portal access",
      "Controllers + Main Admin tiers",
      "FDC (Flight Deck Crew) management",
      "Cabin Crew workspace",
      "Department-scoped teams",
      "Flight Record Logs & document hub",
      "Crew warnings, schedules & records",
      "Standard audit trail",
      "Email & business-hours support",
    ],
  },
  {
    id: "ecp",
    badge: "ENTERPRISE CHOICE",
    name: "ECP — Customized Solution",
    tagline: "Engineered, configured, and deployed to your operation.",
    price: "Bespoke",
    cadence: "enterprise engagement",
    cta: "Request ECP Consultation",
    interest: "ecp",
    featured: true,
    features: [
      "Bespoke modules per role tier",
      "No aircraft, department or user limits",
      "Private deployment (cloud or on-prem)",
      "Dedicated Controller-tier provisioning",
      "Extended Flight Record Logs & retention",
      "Custom document workflows & schedules",
      "Tamper-evident audit trail + export",
      "GACA / NCA / CSCC alignment",
      "24/7 dedicated operations support",
    ],
  },
];

export const FOOTER_LINKS = {
  Platform: ["Hierarchy", "Capabilities", "Compliance", "ECP"],
  Solutions: ["Template Plan", "ECP Enterprise", "Migration", "Onboarding"],
  Company: ["About", "Customers", "Careers", "Press"],
  Resources: ["Documentation", "Status", "Security", "Contact"],
};
